/**
 * Progress under the gallery section's scrolling row: one dot per card, lit
 * while that card is on screen, the same as the product rows' dots
 * (carousel-arrows.js). A row of more than MAX_DOTS cards keeps the
 * continuous line instead.
 *
 * Reads the row's native scroll position rather than keeping its own index,
 * the same way carousel-arrows.js does, so swipes, trackpads and the arrows
 * all agree with the dots.
 */
const MAX_DOTS = 12;

class GalleryProgress extends HTMLElement {
  connectedCallback() {
    this.track = this.closest('.section-gallery')?.querySelector('[data-carousel-track]');
    this.line = this.querySelector('[data-progress-track]');
    this.bar = this.querySelector('[data-progress-bar]');
    this.dots = this.querySelector('[data-progress-dots]');
    if (!this.track) return;

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });

    // Cards grow as their images load, so measure on every resize.
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this.track);

    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.update);
    this.observer?.disconnect();
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;

    // A row that fits has nothing to report.
    this.hidden = scrollWidth - clientWidth <= 1;
    if (this.hidden) return;

    const cards = this.dots ? this.visibleCards() : [];
    const useDots = Boolean(this.dots) && cards.length > 1 && cards.length <= MAX_DOTS;

    if (this.line) this.line.hidden = useDots;
    if (this.dots) this.dots.hidden = !useDots;

    if (!useDots) {
      // The fill covers what has been seen so far, so it starts part-way along
      // (the first screenful) and reaches the end with the last card.
      this.bar.style.transform = `scaleX(${(scrollLeft + clientWidth) / scrollWidth})`;
      return;
    }

    if (this.dots.childElementCount !== cards.length) {
      this.dots.replaceChildren(
        ...cards.map(() => {
          const dot = document.createElement('span');
          dot.className = 'gallery-progress__dot';
          return dot;
        })
      );
    }

    cards.forEach((active, i) => {
      this.dots.children[i].classList.toggle('is-active', active);
    });
  }

  /**
   * One entry per card the visitor can see in the row; true while at least
   * half of it is inside the track. Cards hidden by CSS are left out.
   *
   * @returns {boolean[]}
   */
  visibleCards() {
    const view = this.track.getBoundingClientRect();
    const flags = [];

    for (const card of this.track.children) {
      if (card.getClientRects().length === 0) continue;

      const box = card.getBoundingClientRect();
      if (box.width === 0) continue;

      const shown = Math.min(box.right, view.right) - Math.max(box.left, view.left);
      flags.push(shown >= box.width / 2);
    }

    return flags;
  }
}

if (!customElements.get('gallery-progress')) {
  customElements.define('gallery-progress', GalleryProgress);
}
